import React, { useState, useEffect } from 'react';
import {
  Modal, Form, Button, Row, Col, Alert, Spinner, InputGroup,
  Card, Badge
} from 'react-bootstrap';
import {
  Save, X, User, Phone, MapPin, Mail, Ruler, AlertCircle, Plus, Trash2 
} from 'lucide-react';
import measurementService from '../../../services/measurementService';

/**
 * ✅ ATELIER : Modal de création conjointe "Nouveau client + fiche mesures".
 * 
 * Si un client avec le même téléphone existe déjà, elle sera utilisée
 * (le backend gère la détection).
 */
const NewCustomerMeasurementModal = ({ show, onHide, onSuccess }) => {

  // ===== ÉTATS =====
  const [formData, setFormData] = useState({
    // Client
    customerName: '',
    customerPhone: '',
    customerAddress: '',
    customerEmail: '',
    // Mesures standards (cm)
    tourPoitrine: '',
    tourTaille: '',
    tourHanches: '',
    tourBassin: '',
    longueurRobe: '',
    longueurManche: '',
    longueurJupe: '',
    tourBras: '',
    tourCuisse: '',
    tourCou: '',
    carrureDos: '',
    hauteurEpaule: '',
    cou: '',
    enclure: '',
    notes: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

    // ✅ AJOUT : Liste des mesures personnalisées (nom + valeur)
  const [customMeasurements, setCustomMeasurements] = useState([]);

  // Reset form quand le modal s'ouvre
  useEffect(() => {
    if (show) {
      setFormData({
        customerName: '',
        customerPhone: '',
        customerAddress: '',
        customerEmail: '',
        tourPoitrine: '',
        tourTaille: '',
        tourHanches: '',
        tourBassin: '',
        longueurRobe: '',
        longueurManche: '',
        longueurJupe: '',
        tourBras: '',
        tourCuisse: '',
        tourCou: '',
        carrureDos: '',
        hauteurEpaule: '',
        cou: '',
        enclure: '',
        notes: ''
      });
      setError(null);
      setCustomMeasurements([]); // ✅ AJOUT : Reset mesures custom
    }
  }, [show]);

  // ===== HANDLERS =====

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // N'accepter que des chiffres et un point décimal
  const handleNumberChange = (field, value) => {
    if (value === '' || /^\d*\.?\d*$/.test(value)) {
      handleChange(field, value);
    }
  };

  // ✅ Ajouter une nouvelle mesure personnalisée vide
const handleAddCustomMeasurement = () => {
  setCustomMeasurements(prev => [
    ...prev,
    { id: Date.now(), name: '', value: '' }
  ]);
};

// ✅ Modifier une mesure personnalisée (nom ou valeur)
const handleCustomMeasurementChange = (id, field, value) => {
  // Pour le champ "value", on n'accepte que les chiffres et le point décimal
  if (field === 'value' && value !== '' && !/^\d*\.?\d*$/.test(value)) {
    return;
  }
  
  setCustomMeasurements(prev =>
    prev.map(m => m.id === id ? { ...m, [field]: value } : m)
  );
};

// ✅ Supprimer une mesure personnalisée
const handleRemoveCustomMeasurement = (id) => {
  setCustomMeasurements(prev => prev.filter(m => m.id !== id));
};

  // Validation
  const validate = () => {
    if (!formData.customerName.trim()) {
      return 'Le nom du client est obligatoire';
    }
    if (formData.customerName.trim().length < 2) {
      return 'Le nom doit contenir au moins 2 caractères';
    }
    if (!formData.customerPhone.trim()) {
      return 'Le numéro de téléphone est obligatoire';
    }

    // Au moins une mesure doit être renseignée
    const measurementFields = [
      'tourPoitrine', 'tourTaille', 'tourHanches', 'tourBassin',
      'longueurRobe', 'longueurManche', 'longueurJupe', 'tourBras',
      'tourCuisse', 'tourCou', 'carrureDos', 'hauteurEpaule', 'cou',
      'enclure'
    ];
    // Vérifier qu'au moins une mesure standard OU personnalisée est renseignée
const hasStandardMeasurement = measurementFields.some(
  f => formData[f] !== '' && parseFloat(formData[f]) > 0
);

const hasValidCustomMeasurement = customMeasurements.some(
  m => m.name.trim() && m.value !== '' && parseFloat(m.value) > 0
);

if (!hasStandardMeasurement && !hasValidCustomMeasurement) {
  return 'Au moins une mesure (standard ou personnalisée) doit être renseignée';
}

// Vérifier que les mesures custom ont un nom si elles ont une valeur
const customWithValueButNoName = customMeasurements.find(
  m => m.value !== '' && !m.name.trim()
);
if (customWithValueButNoName) {
  return 'Toutes les mesures personnalisées doivent avoir un nom';
}

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setLoading(true);

    try {
  // ✅ Convertir les mesures custom du tableau au format objet attendu par le backend
  const customMeasurementsObject = {};
  customMeasurements.forEach(m => {
    if (m.name.trim() && m.value !== '') {
      customMeasurementsObject[m.name.trim()] = parseFloat(m.value);
    }
  });

  // Préparer les données avec les custom mesures
  const dataToSend = {
    ...formData,
    customMeasurements: customMeasurementsObject
  };

  const response = await measurementService.createMeasurementWithNewCustomer(dataToSend);

      if (response.success) {
        console.log('✅ Fiche + client crées:', response.data);
        // On notifie le parent avec la fiche créée + le message du backend
        onSuccess?.(response.data, response.message);
        onHide();
      } else {
        setError(response.message || 'Erreur lors de la création');
      }
    } catch (err) {
      console.error('❌ Erreur:', err);
      setError(err.message || 'Erreur inattendue');
    } finally {
      setLoading(false);
    }
  };

  // ===== RENDU =====

  return (
    <Modal show={show} onHide={onHide} size="lg" centered backdrop="static" scrollable>
      <Modal.Header closeButton className="bg-primary text-white">
        <Modal.Title className="d-flex align-items-center gap-2">
          <User size={22} />
          Nouveau client + fiche de mesures
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              <AlertCircle size={16} className="me-2" />
              {error}
            </Alert>
          )}

          <Alert variant="info" className="small d-flex align-items-start gap-2">
            <span style={{ fontSize: '1.1rem' }}>💡</span>
            <div>
              Si un client avec ce <strong>numéro de téléphone</strong> existe déjà, 
              la fiche de mesures lui sera ajoutée automatiquement (le client ne sera 
              pas dupliquée).
            </div>
          </Alert>

          {/* ===== SECTION CLIENT ===== */}
          <div className="mb-4">
            <h6 className="d-flex align-items-center gap-2 text-primary mb-3 border-bottom pb-2">
              <User size={18} />
              1. Informations client
            </h6>

            <Row className="g-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">
                    Nom complet <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Ex: Aminata Diop"
                    value={formData.customerName}
                    onChange={(e) => handleChange('customerName', e.target.value)}
                    autoFocus
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">
                    <Phone size={12} className="me-1" />
                    Téléphone <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="tel"
                    placeholder="Ex: +221 77 123 45 67"
                    value={formData.customerPhone}
                    onChange={(e) => handleChange('customerPhone', e.target.value)}
                  />
                </Form.Group>
              </Col>

              <Col md={6}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">
                    <Mail size={12} className="me-1" />
                    Email
                  </Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="Optionnel"
                    value={formData.customerEmail}
                    onChange={(e) => handleChange('customerEmail', e.target.value)}
                  />
                </Form.Group>
              </Col>

              <Col md={12}>
                <Form.Group>
                  <Form.Label className="small fw-semibold">
                    <MapPin size={12} className="me-1" />
                    Adresse
                  </Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Ex: Médina, Dakar (optionnel)"
                    value={formData.customerAddress}
                    onChange={(e) => handleChange('customerAddress', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>
          </div>

          {/* ===== SECTION MESURES STANDARDS ===== */}
          <div className="mb-4">
            <h6 className="d-flex align-items-center gap-2 text-primary mb-3 border-bottom pb-2">
              <Ruler size={18} />
              2. Mesures (en cm) — au moins une obligatoire
            </h6>

            <Row className="g-3">
              <MeasurementInput
                label="Epaul" field="tourPoitrine"
                value={formData.tourPoitrine}
                onChange={handleNumberChange}
              />
              <MeasurementInput
                label="Pointrine" field="tourTaille"
                value={formData.tourTaille}
                onChange={handleNumberChange}
              />
              <MeasurementInput
                label="Taille" field="tourHanches"
                value={formData.tourHanches}
                onChange={handleNumberChange}
              />
              <MeasurementInput
                label="Ventre" field="tourBassin"
                value={formData.tourBassin}
                onChange={handleNumberChange}
              />
              <MeasurementInput
                label="Tour de hanche" field="longueurRobe"
                value={formData.longueurRobe}
                onChange={handleNumberChange}
              />
              <MeasurementInput
                label="Pince" field="longueurManche"
                value={formData.longueurManche}
                onChange={handleNumberChange}
              />
              <MeasurementInput
                label="Blouse" field="longueurJupe"
                value={formData.longueurJupe}
                onChange={handleNumberChange}
              />
              <MeasurementInput
                label="Longueur robe" field="tourBras"
                value={formData.tourBras}
                onChange={handleNumberChange}
              />
              <MeasurementInput
                label="Epaul manche" field="tourCuisse"
                value={formData.tourCuisse}
                onChange={handleNumberChange}
              />
              <MeasurementInput
                label="Manche" field="tourCou"
                value={formData.tourCou}
                onChange={handleNumberChange}
              />
              <MeasurementInput
                label="Tour manche" field="carrureDos"
                value={formData.carrureDos}
                onChange={handleNumberChange}
              />
              <MeasurementInput
                label="Longueur pagne" field="hauteurEpaule"
                value={formData.hauteurEpaule}
                onChange={handleNumberChange}
              />
              <MeasurementInput
                label="Cou" field="cou"
                value={formData.cou}
                onChange={handleNumberChange}
              />
              <MeasurementInput
                label="Enclure" field="enclure"
                value={formData.enclure}
                onChange={handleNumberChange}
              />
            </Row>
          </div>

          {/* ===== SECTION MESURES PERSONNALISÉES ===== */}
<div className="mb-4">
  <div className="d-flex justify-content-between align-items-center border-bottom pb-2 mb-3">
    <h6 className="d-flex align-items-center gap-2 text-primary mb-0">
      <Ruler size={18} />
      3. Mesures personnalisées
      <Badge bg="secondary" pill style={{ fontSize: '0.65rem' }}>
        Optionnel
      </Badge>
    </h6>
    <Button
      type="button"
      variant="outline-primary"
      size="sm"
      onClick={handleAddCustomMeasurement}
    >
      <Plus size={14} className="me-1" />
      Ajouter
    </Button>
  </div>

  {customMeasurements.length === 0 ? (
    <div className="text-center text-muted py-2 small fst-italic">
      Aucune mesure personnalisée. Cliquez sur "Ajouter" pour en créer une 
      (ex: Longueur boubou, Hauteur de pince...).
    </div>
  ) : (
    <div className="d-flex flex-column gap-2">
      {customMeasurements.map((measurement, idx) => (
        <Row key={measurement.id} className="g-2 align-items-center">
          <Col md={6}>
            <Form.Control
              type="text"
              size="sm"
              placeholder={`Nom mesure ${idx + 1} (ex: Longueur boubou)`}
              value={measurement.name}
              onChange={(e) =>
                handleCustomMeasurementChange(measurement.id, 'name', e.target.value)
              }
            />
          </Col>
          <Col md={5}>
            <InputGroup size="sm">
              <Form.Control
                type="text"
                inputMode="decimal"
                placeholder="0"
                value={measurement.value}
                onChange={(e) =>
                  handleCustomMeasurementChange(measurement.id, 'value', e.target.value)
                }
              />
              <InputGroup.Text>cm</InputGroup.Text>
            </InputGroup>
          </Col>
          <Col md={1} className="text-center">
            <Button
              type="button"
              variant="outline-danger"
              size="sm"
              onClick={() => handleRemoveCustomMeasurement(measurement.id)}
              title="Supprimer cette mesure"
            >
              <Trash2 size={14} />
            </Button>
          </Col>
        </Row>
      ))}
    </div>
  )}
</div>

          {/* ===== NOTES ===== */}
          <Form.Group className="mb-2">
  <Form.Label className="small fw-semibold">4. Notes</Form.Label>
            <Form.Control
              as="textarea"
              rows={2}
              placeholder="Préférences du client, particularités..."
              value={formData.notes}
              onChange={(e) => handleChange('notes', e.target.value)}
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={loading}>
            <X size={16} className="me-1" />
            Annuler
          </Button>
          <Button type="submit" variant="primary" disabled={loading}>
            {loading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Enregistrement...
              </>
            ) : (
              <>
                <Save size={16} className="me-1" />
                Enregistrer
              </>
            )}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

// ============================================================
// =========== Petit composant pour les mesures ===============
// ============================================================

const MeasurementInput = ({ label, field, value, onChange }) => (
  <Col md={4} sm={6}>
    <Form.Group>
      <Form.Label className="small">{label}</Form.Label>
      <InputGroup size="sm">
        <Form.Control
          type="text"
          inputMode="decimal"
          placeholder="0"
          value={value}
          onChange={(e) => onChange(field, e.target.value)}
        />
        <InputGroup.Text>cm</InputGroup.Text>
      </InputGroup>
    </Form.Group>
  </Col>
);

export default NewCustomerMeasurementModal;